import React, { useState, useEffect } from 'react';
import { apiService } from '../services';
import { Ranking as RankingType, RankingItem } from '../types/ranking';
import {
  WorkoutResult,
  AMRAPResult,
  ForTimeResult,
  EMOMResult,
  MaxTimeResult,
  MaxWeightResult
} from '../types/result';

interface Event {
  id: string;
  name: string;
}

const Ranking: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [rankings, setRankings] = useState<RankingType[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadRankings();
    }
  }, [selectedEventId, selectedGender, selectedCategory]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await apiService.get<Event[]>('/events');
      setEvents(data);
    } catch (err: any) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async () => {
    try {
      setLoadingRankings(true);

      // Build query params
      const params = new URLSearchParams();
      if (selectedGender) params.append('gender', selectedGender);
      if (selectedCategory) params.append('category', selectedCategory);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const data = await apiService.get<RankingType[]>(`/events/${selectedEventId}/rankings${queryString}`);
      setRankings(data);
      setActiveTab(0);
    } catch (err: any) {
      console.error('Failed to load rankings:', err);
      setRankings([]);
    } finally {
      setLoadingRankings(false);
    }
  };

  const getMedalIcon = (position: number) => {
    switch (position) {
      case 1:
        return <span className="text-2xl">🥇</span>;
      case 2:
        return <span className="text-2xl">🥈</span>;
      case 3:
        return <span className="text-2xl">🥉</span>;
      default:
        return null;
    }
  };

  const getPositionClass = (position: number) => {
    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 text-yellow-900 border-l-4 border-yellow-400';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-900 border-l-4 border-gray-400';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-amber-50 text-amber-900 border-l-4 border-amber-400';
      default:
        return 'bg-white text-gray-900';
    }
  };

  const formatWorkoutResult = (result: WorkoutResult | undefined): string => {
    if (!result) return 'No result';

    switch (result.type) {
      case 'AMRAP': {
        const amrapResult = result as AMRAPResult;
        return `${amrapResult.reps} reps`;
      }
      case 'FOR_TIME': {
        const forTimeResult = result as ForTimeResult;
        if (forTimeResult.cap) {
          // DNF - show CAP + reps
          const reps = forTimeResult.reps || 0;
          return `CAP+${reps}`;
        } else {
          // Completed - show time
          const mins = forTimeResult.mins || 0;
          const secs = forTimeResult.seconds || 0;
          return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
      }
      case 'EMOM': {
        const emomResult = result as EMOMResult;
        const lastReps = emomResult.lastRoundReps ? `+${emomResult.lastRoundReps}` : '';
        return `${emomResult.roundsCompleted} rounds${lastReps}`;
      }
      case 'MAX_TIME': {
        const maxTimeResult = result as MaxTimeResult;
        return `${maxTimeResult.mins}:${maxTimeResult.seconds.toString().padStart(2, '0')}`;
      }
      case 'MAX_WEIGHT': {
        const maxWeightResult = result as MaxWeightResult;
        return `${maxWeightResult.weight} lbs`;
      }
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Rankings</h1>
        <p className="text-gray-600 mt-2">View competition rankings and leaderboards</p>
      </div>

      {/* Event Selector */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Event
        </label>
        <select
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          className="w-full max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
        >
          <option value="">-- Choose an event --</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
      </div>

      {/* Filters */}
      {selectedEventId && (
        <div className="mb-6 flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender
            </label>
            <select
              value={selectedGender}
              onChange={(e) => setSelectedGender(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Genders</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              <option value="RX">RX</option>
              <option value="SCALED">Scaled</option>
            </select>
          </div>
        </div>
      )}

      {/* No Event Selected */}
      {!selectedEventId && !loading && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No event selected</h3>
          <p className="mt-2 text-gray-600">Select an event to view rankings</p>
        </div>
      )}

      {/* Loading Rankings */}
      {loadingRankings && (
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {/* Rankings Display */}
      {selectedEventId && !loadingRankings && rankings.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No rankings available</h3>
          <p className="mt-2 text-gray-600">Complete some workouts to generate rankings</p>
        </div>
      )}

      {selectedEventId && !loadingRankings && rankings.length > 0 && (
        <>
          {/* Public View Button */}
          <div className="mb-6 flex justify-end">
            <a
              href={`/public/ranking/${selectedEventId}${
                selectedGender || selectedCategory
                  ? `?${selectedGender ? `gender=${selectedGender}` : ''}${
                      selectedGender && selectedCategory ? '&' : ''
                    }${selectedCategory ? `category=${selectedCategory}` : ''}`
                  : ''
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View Public Ranking
            </a>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-4 overflow-x-auto">
              {rankings.map((ranking, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === index
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  {ranking.name}
                  {ranking.name === 'General' && (
                    <svg className="inline-block h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Ranking Content */}
          {rankings[activeTab] && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
                <h2 className="text-xl font-bold text-white">{rankings[activeTab].name} Ranking</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {rankings[activeTab].results.map((item, index) => (
                  <div
                    key={index}
                    className={`px-6 py-4 transition-all hover:shadow-md ${getPositionClass(item.position)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Position */}
                        <div className="flex items-center justify-center w-16">
                          {getMedalIcon(item.position) || (
                            <span className="text-2xl font-bold text-gray-700">#{item.position}</span>
                          )}
                        </div>

                        {/* Name */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold">{item.name}</h3>

                          {/* Result (for workout rankings) */}
                          {item.result && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-white bg-opacity-60">
                                {formatWorkoutResult(item.result)}
                              </span>
                            </div>
                          )}

                          {/* Workout Results (for General ranking) */}
                          {item.workoutResults.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {item.workoutResults.map((workoutResult, idx) => (
                                <div
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 bg-white bg-opacity-50 rounded text-xs"
                                >
                                  <span className="font-medium">{workoutResult.name}:</span>
                                  <span className="ml-1">#{workoutResult.position} ({workoutResult.score.toFixed(1)} pts)</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Tiebreaker Breakdown */}
                          {item.tiebreakerBreakdown && item.tiebreakerBreakdown.length > 0 && (
                            <div className="mt-2">
                              <div className="flex items-start gap-1.5">
                                <svg className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div className="flex flex-col gap-0.5">
                                  {item.tiebreakerBreakdown.map((breakdownItem, idx) => (
                                    <p key={idx} className="text-[10px] text-gray-600 italic leading-tight">
                                      {breakdownItem}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Score */}
                        <div className="text-right">
                          <div className="text-sm text-gray-600 font-medium">
                            {rankings[activeTab].name === 'General' ? 'Total Points' : 'Points'}
                          </div>
                          <div className="text-2xl font-bold">
                            {rankings[activeTab].name === 'General' ? item.score.toFixed(1) : item.score.toFixed(0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Ranking;
